"use client"

import styled from "@emotion/styled"
import { GetPageByRouteQuery } from "@eurodrive/apollo"
import { CustomBreadcrumbs } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { IconButton, Typography } from "@mui/material"
import Image from "next/image"
import RichTextBody from "./RichTextBody"
import {
  getRichTextBreadcrumbs,
  getRichTextPageContent,
} from "./richTextPageContent"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
    padding-bottom: 64px;
  }
`

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    max-width: 800px;
    margin: 0 auto;
  }
`

const IntroBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

/**
 * Per the design the share button sits at the inline-start on mobile and flips to the
 * inline-end from md up.
 */
const ShareRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-inline: -8px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: flex-end;
  }
`

const StyledIconButton = styled(IconButton)`
  padding: 8px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default function RichTextPage({
  data,
  locale,
}: {
  data: GetPageByRouteQuery
  locale: string
}) {
  const { title, sections } = getRichTextPageContent(data)
  const breadcrumbs = getRichTextBreadcrumbs(data, locale)

  const handleShare = async () => {
    const currentUrl = window.location.href

    try {
      if (navigator.share) {
        await navigator.share({ title, url: currentUrl })
      } else {
        await navigator.clipboard.writeText(currentUrl)
      }
    } catch {
      // The share sheet was dismissed or the clipboard was blocked — nothing to recover.
    }
  }

  return (
    <Container paddingMobile>
      <StyledLayout>
        <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
        <MainContainer>
          <IntroBox>
            {title && (
              <Typography variant="h1" component="h1">
                {title}
              </Typography>
            )}
            <ShareRow>
              <StyledIconButton onClick={handleShare}>
                <Image
                  src="/icons/shareButton.svg"
                  width={24}
                  height={24}
                  alt="שתף עמוד"
                />
              </StyledIconButton>
            </ShareRow>
          </IntroBox>
          {sections.map((section, index) => (
            <Section key={index}>
              {index > 0 && section.title && (
                <Typography variant="h2" component="h2">
                  {section.title}
                </Typography>
              )}
              {section.content && <RichTextBody content={section.content} />}
            </Section>
          ))}
        </MainContainer>
      </StyledLayout>
    </Container>
  )
}
